import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeroProps = {
  language: Language;
};
const content = {
  ua: {
    headline: 'CHAMÄLEON',
    subheadline: 'Преміальний бренд покриттів та автохімії',
    description: '60 років досвіду. Присутні у 80 країнах. Професійні рішення для дистрибуції, ритейлу та кузовних сервісів.',
    cta1: 'Переглянути продукцію',
    cta2: 'Стати партнером'
  },
  en: {
    headline: 'CHAMÄLEON',
    subheadline: 'Premium Coatings & Auto Chemistry Brand',
    description: '60 years of experience. Present in 80 countries. Professional solutions for distribution, retail, and body shops.',
    cta1: 'View Products',
    cta2: 'Become a Partner'
  }
};
// Abstract visual component replaced with Video
function HeroVideo() {
  return <div className="relative w-full max-w-sm mx-auto" style={{
    aspectRatio: '9/16'
  }} data-id="element-158">
      {/* Main visual container */}
      <motion.div className="absolute inset-0 rounded-3xl overflow-hidden border border-white/10 shadow-premium" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 1,
      ease: 'easeOut'
    }} data-id="element-159">
        <video src="https://cdn.magicpatterns.com/uploads/whbfRcfNb4CATKdeDKxFBE/img-7800_TqaL5a7D.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-160" />
        <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-black/80 via-transparent to-transparent pointer-events-none" data-id="element-161" />
      </motion.div>
    </div>;
}
export function Hero({
  language
}: HeroProps) {
  const t = content[language];
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-chamaleon-black" data-id="element-162">
      {/* Modern Background Effects */}
      <div className="absolute inset-0" data-id="element-163">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} data-id="element-164" />
        <div className="absolute inset-0 bg-gradient-to-br from-chamaleon-black via-chamaleon-black to-chamaleon-charcoal" data-id="element-165" />
        <motion.div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] rounded-full opacity-[0.08] pointer-events-none" style={{
        background: 'radial-gradient(circle, #FFB81C 0%, transparent 70%)'
      }} animate={{
        scale: [1, 1.1, 1],
        opacity: [0.08, 0.12, 0.08]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-166" />
        <div className="absolute top-1/4 right-0 w-px h-64 bg-gradient-to-b from-transparent via-chamaleon-yellow/20 to-transparent" data-id="element-167" />
        <div className="absolute bottom-1/4 left-0 w-px h-64 bg-gradient-to-b from-transparent via-chamaleon-yellow/20 to-transparent" data-id="element-168" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40" data-id="element-169">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-170">
          {/* Content - Left Side */}
          <div className="max-w-2xl mx-auto lg:mx-0" data-id="element-171">
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-none mb-4 sm:mb-6" data-id="element-172">
              {t.headline}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="text-xl sm:text-2xl lg:text-3xl text-chamaleon-yellow font-light mb-6 sm:mb-8 lg:mb-6" data-id="element-173">
              {t.subheadline}
            </motion.p>

            {/* Mobile Visual - Show after subheadline on mobile only */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} className="lg:hidden mb-8 sm:mb-10" data-id="element-174">
              <HeroVideo data-id="element-175" />
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="text-base sm:text-lg text-gray-400 mb-8 sm:mb-12 leading-relaxed" data-id="element-176">
              {t.description}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-177">
              <motion.a href="#products" className="group relative inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-chamaleon-yellow text-chamaleon-black font-semibold rounded-lg overflow-hidden text-sm sm:text-base" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-178">
                <span className="relative z-10" data-id="element-179">{t.cta1}</span>
                <ArrowRightIcon className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-180" />
              </motion.a>

              <motion.a href="#partner" className="group inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 hover:border-chamaleon-yellow transition-all duration-300 text-sm sm:text-base" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-181">
                {t.cta2}
                <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-182" />
              </motion.a>
            </motion.div>
          </div>

          {/* Visual - Right Side - Desktop Only */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.7
        }} className="relative hidden lg:block" data-id="element-183">
            <HeroVideo data-id="element-184" />
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-chamaleon-black to-transparent pointer-events-none" data-id="element-185" />
    </section>;
}